import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
// Mock data - replace with actual user data
const USER_PATTERN = 'You tend to take responsibility early and push through resistance.';
// Mock timeline entries with different types
const ENTRIES = [{
  id: 1,
  date: 'Mon, Jan 15',
  type: 'note',
  userNote: 'Had a tense review meeting with the product team. I pushed hard for shipping the feature early. They pushed back about quality concerns and the room went quiet.',
  mirrorReflection: 'This repeats your pattern of pushing for resolution under disagreement. You optimized for speed over alignment.'
}, {
  id: 2,
  date: 'Fri, Jan 12',
  type: 'situation',
  situationData: {
    situationType: 'Conflict',
    role: 'Driving it forward',
    tension: 'Speed vs quality',
    outcome: 'Still unresolved',
    context: 'Weekly review with product team'
  },
  mirrorReflection: 'This fits your tendency to step in and push for resolution when a situation remains ambiguous. The tension between speed and alignment shows up repeatedly in how you operate, especially when responsibility is not clearly held. The situation remaining unresolved keeps the same loop active rather than closing it.'
}, {
  id: 3,
  date: 'Thu, Jan 11',
  type: 'mode',
  modeData: 'Pushing, Carrying',
  mirrorReflection: 'This repeats your pattern of prioritizing momentum over alignment. You were in execution mode, not collaborative mode.'
}];
// User state
const HAS_ENTRIES = ENTRIES.length > 0;
export function WorkMirrorAppHome() {
  const navigate = useNavigate();
  const handleNoteEntry = () => {
    navigate('/work-mirror/first-entry');
  };
  const handleSituationDecode = () => {
    navigate('/work-mirror/decode-situation');
  };
  const handleModeLog = () => {
    navigate('/work-mirror/log-mode');
  };
  return <>
      <Helmet data-id="element-3983">
        <title data-id="element-3984">Your Work Mirror | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-3985" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-3986">
        {/* Minimal header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-[#2B2B2B]" data-id="element-3987">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3988">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3989">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs" className="h-6 w-auto" width="24" height="24" data-id="element-3990" />
              <span className="text-sm font-mono text-gray-500" data-id="element-3991">
                Work Mirror
              </span>
            </Link>

            <button className="text-sm text-gray-600 hover:text-gray-400 transition-colors" data-id="element-3992">
              Settings
            </button>
          </div>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-3993">
          <div className="max-w-3xl mx-auto space-y-16" data-id="element-3994">
            {/* Pattern Plaque (Invariant) */}
            <section className="space-y-6 pt-12" data-id="element-3995">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-widest" data-id="element-3996">
                Your pattern
              </p>

              <p className="text-xl md:text-2xl text-[#F5F5F5] leading-relaxed font-light" data-id="element-3997">
                {USER_PATTERN}
              </p>
            </section>

            {/* Primary Action Area - Three Input Surfaces */}
            <section className="pt-8 space-y-8" data-id="element-3998">
              <h2 className="text-base font-medium text-[#F5F5F5]" data-id="element-3999">
                Add to your mirror
              </h2>

              <div className="space-y-4" data-id="element-4000">
                {/* 1. Note what happened */}
                <button onClick={handleNoteEntry} className="w-full text-left p-6 border border-[#2B2B2B] hover:border-[#64748B] transition-colors bg-[#0A0A0A]" data-id="element-4001">
                  <div className="space-y-2" data-id="element-4002">
                    <h3 className="text-base font-medium text-[#F5F5F5]" data-id="element-4003">
                      Note what happened
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4004">
                      Write a few lines about something from today.
                    </p>
                  </div>
                </button>

                {/* 2. Decode a situation */}
                <button onClick={handleSituationDecode} className="w-full text-left p-6 border border-[#2B2B2B] hover:border-[#64748B] transition-colors bg-[#0A0A0A]" data-id="element-4005">
                  <div className="space-y-2" data-id="element-4006">
                    <h3 className="text-base font-medium text-[#F5F5F5]" data-id="element-4007">
                      Decode a situation
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4008">
                      Log the shape of a specific situation in 30 seconds.
                    </p>
                  </div>
                </button>

                {/* 3. Log today's mode */}
                <button onClick={handleModeLog} className="w-full text-left p-6 border border-[#2B2B2B] hover:border-[#64748B] transition-colors bg-[#0A0A0A]" data-id="element-4009">
                  <div className="space-y-2" data-id="element-4010">
                    <h3 className="text-base font-medium text-[#F5F5F5]" data-id="element-4011">
                      Log today's mode
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4012">
                      Just mark what mode you were mostly in today.
                    </p>
                  </div>
                </button>
              </div>
            </section>

            {/* Timeline / History */}
            <section className="space-y-12 pt-12 border-t border-[#2B2B2B]" data-id="element-4013">
              {!HAS_ENTRIES ?
            // Empty state
            <div className="py-16" data-id="element-4014">
                  <p className="text-base text-gray-600 leading-relaxed" data-id="element-4015">
                    Your mirror will start filling up as you use it.
                  </p>
                </div> :
            // Timeline with entries
            <div className="space-y-12" data-id="element-4016">
                  {ENTRIES.map(entry => <div key={entry.id} className="space-y-6" data-id="element-4017">
                      {/* Date */}
                      <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-4018">
                        {entry.date}
                      </p>

                      {/* Entry content based on type */}
                      {entry.type === 'note' && <>
                          <div className="space-y-3" data-id="element-4019">
                            <p className="text-sm text-gray-600" data-id="element-4020">You wrote:</p>
                            <p className="text-base text-gray-400 leading-relaxed" data-id="element-4021">
                              {entry.userNote}
                            </p>
                          </div>

                          <div className="pl-6 border-l-2 border-[#2B2B2B]" data-id="element-4022">
                            <p className="text-sm text-gray-600 mb-3" data-id="element-4023">Mirror</p>
                            <p className="text-base text-[#F5F5F5] leading-relaxed" data-id="element-4024">
                              {entry.mirrorReflection}
                            </p>
                          </div>
                        </>}

                      {entry.type === 'situation' && <>
                          {/* Part 1 — The Recorded Situation */}
                          <div className="space-y-4" data-id="element-4025">
                            <p className="text-sm text-gray-600" data-id="element-4026">Situation</p>

                            <div className="space-y-2 text-sm" data-id="element-4027">
                              <p className="text-gray-500" data-id="element-4028">
                                <span className="text-gray-600" data-id="element-4029">Type: </span>
                                <span className="text-gray-400" data-id="element-4030">
                                  {entry.situationData.situationType}
                                </span>
                              </p>
                              <p className="text-gray-500" data-id="element-4031">
                                <span className="text-gray-600" data-id="element-4032">Role: </span>
                                <span className="text-gray-400" data-id="element-4033">
                                  {entry.situationData.role}
                                </span>
                              </p>
                              <p className="text-gray-500" data-id="element-4034">
                                <span className="text-gray-600" data-id="element-4035">Tension: </span>
                                <span className="text-gray-400" data-id="element-4036">
                                  {entry.situationData.tension}
                                </span>
                              </p>
                              <p className="text-gray-500" data-id="element-4037">
                                <span className="text-gray-600" data-id="element-4038">Outcome: </span>
                                <span className="text-gray-400" data-id="element-4039">
                                  {entry.situationData.outcome}
                                </span>
                              </p>

                              {/* Optional context */}
                              {entry.situationData.context && <p className="text-xs text-gray-600 pt-2" data-id="element-4040">
                                  {entry.situationData.context}
                                </p>}
                            </div>
                          </div>

                          {/* Part 2 — The Mirror Annotation */}
                          <div className="pl-6 border-l-2 border-[#2B2B2B]" data-id="element-4041">
                            <p className="text-sm text-gray-600 mb-3" data-id="element-4042">Mirror</p>
                            <div className="space-y-4 text-base text-[#F5F5F5] leading-relaxed" data-id="element-4043">
                              {entry.mirrorReflection.split('\n\n').map((paragraph, i) => <p key={i} data-id="element-4044">{paragraph}</p>)}
                            </div>
                          </div>
                        </>}

                      {entry.type === 'mode' && <>
                          <div className="space-y-3" data-id="element-4045">
                            <p className="text-sm text-gray-600" data-id="element-4046">
                              You logged your mode:
                            </p>
                            <p className="text-xl text-gray-400 font-light" data-id="element-4047">
                              {entry.modeData}
                            </p>
                          </div>

                          <div className="pl-6 border-l-2 border-[#2B2B2B]" data-id="element-4048">
                            <p className="text-sm text-gray-600 mb-3" data-id="element-4049">Mirror</p>
                            <p className="text-base text-[#F5F5F5] leading-relaxed" data-id="element-4050">
                              {entry.mirrorReflection}
                            </p>
                          </div>
                        </>}
                    </div>)}
                </div>}
            </section>
          </div>
        </main>
      </div>
    </>;
}